import { Component, OnInit, ViewChild, Optional, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SessionService } from '@shared/helpers/sesion.service';
import { SwalService } from '@shared/utils/swal.service';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CentroMensajesService } from './services/centro-mensajes.service';
import { 
  IOpcionVariable, 
  IMensajeNotificado, 
  IMensajeEnviado,
  IUsuario,
  IEnviarMensajeNotifica1Request
} from './interfaces/centro-mensajes.interface';
import Swal from 'sweetalert2';

interface ITableColumn {
  field: string;
  header: string;
}

@Component({
  selector: 'app-centro-mensajes',
  templateUrl: './centro-mensajes.component.html',
  styleUrls: ['./centro-mensajes.component.html']
})
export class CentroMensajesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  messageForm: FormGroup;
  currentUser: any;
  username: string = '';
  perfiles: IOpcionVariable[] = [];
  perfilesDropdown: any[] = [];
  usuarios: IUsuario[] = [];
  dataSource: MatTableDataSource<IMensajeNotificado | IMensajeEnviado>;
  fechaDesde: Date | string | null = null;
  fechaHasta: Date | null = null;
  tiposMensaje = [
    { label: 'Recibidos', value: 'recibidos' },
    { label: 'Enviados', value: 'enviados' }
  ];
  tipoSeleccionado: string = 'recibidos';
  loading: boolean = false;
  selectedPerfil: string | null = null;
  todosPerfiles: boolean = false;
  selectedUsuario: string | null = null;
  mensaje: string = '';
  mensajes: any[] = [];

  readonly columns: ITableColumn[] = [
    { field: 'deusuario', header: 'De' },
    { field: 'nombreUsuario', header: 'Usuario' },
    { field: 'fecha', header: 'Fecha' },
    { field: 'hora', header: 'Hora' },
    { field: 'mensaje', header: 'Mensaje' },
    { field: 'fechalectura', header: 'Fecha Lectura' },
    { field: 'horalectura', header: 'Hora Lectura' }
  ];
  modoActividad: boolean = false;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<CentroMensajesComponent>,
    private formBuilder: FormBuilder,
    private sessionService: SessionService,
    private centroMensajesService: CentroMensajesService,
    private swalService: SwalService
  ) {
    this.messageForm = this.initializeForm();
    this.dataSource = new MatTableDataSource();
    this.cargarDatosIniciales();
  }

  ngOnInit(): void {
    this.cargarDatos();
    this.modoActividad = this.data?.modoActividad || false;
    
    // Establecer fecha actual y ejecutar búsqueda automática
    this.fechaDesde = new Date();
    this.buscarMensajes();
  }

  private cargarDatos(): void {
    this.loading = true;
    const requests = {
      perfiles: this.centroMensajesService.getOpcionesVariables('usuarios', 'clasificador'),
      usuarios: this.centroMensajesService.getUsuarios()
    };

    forkJoin(requests)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response: any) => {
          if (response.perfiles?.success) {
            this.perfiles = response.perfiles.data;
            this.perfilesDropdown = this.perfiles.map(perfil => ({
              label: perfil.sOpcion,
              value: perfil.sValor
            }));
          }
          if (response.usuarios?.success) {
            this.usuarios = response.usuarios.data;
          }
        },
        error: (error) => {
          console.error('Error al cargar datos:', error);
        }
      });
  }

  private initializeForm(): FormGroup {
    return this.formBuilder.group({
      destinatario: ['', Validators.required],
      mensaje: ['', Validators.required],
      perfil: ['', Validators.required]
    });
  }

  private cargarDatosIniciales(): void {
    this.currentUser = this.sessionService.getUserSession().replace('"', '');
    this.username = this.currentUser?.username || '';
  }

  enviarMensaje(): void {
    if (!this.mensaje?.trim()) {
      this.swalService.warning('El mensaje es requerido');
      return;
    }

    if (!this.selectedUsuario && !this.selectedPerfil && !this.todosPerfiles) {
      this.swalService.warning('Debe seleccionar un usuario o perfil');
      return;
    }

    let destinatarioMostrar = '';
    if (this.todosPerfiles) {
      destinatarioMostrar = 'Todos los usuarios';
    } else if (this.selectedUsuario) {
      const usuario = this.usuarios.find(u => u.susuario === this.selectedUsuario);
      destinatarioMostrar = `Usuario: ${usuario?.nombreusuario || this.selectedUsuario}`;
    } else if (this.selectedPerfil) {
      const perfil = this.perfilesDropdown.find(p => p.value === this.selectedPerfil);
      destinatarioMostrar = `Perfil: ${perfil?.label || this.selectedPerfil}`;
    }

    const mensajeConfirmacion = `
      <div style="text-align: left">
        <p><strong>Destinatario:</strong> ${destinatarioMostrar}</p>
        <p><strong>Mensaje:</strong> ${this.mensaje.trim()}</p>
      </div>
    `;

    Swal.fire({
      title: 'Confirmar envío',
      html: mensajeConfirmacion,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
      heightAuto: false
    }).then((result) => {
      if (result.isConfirmed) {
        this.enviarMensajeConfirmado();
      }
    });
  }

  private enviarMensajeConfirmado(): void {
    const request: IEnviarMensajeNotifica1Request = {
      sUsuario: this.sessionService.getUserSession().replace(/['"]+/g, ''),
      sUsuPerf: this.todosPerfiles ? '*' : (this.selectedUsuario || this.selectedPerfil || ''),
      sMensaje: this.mensaje.trim()
    };

    this.loading = true;
    this.centroMensajesService.enviarMensajeNotifica1(request)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.error === 0) {
            this.swalService.success('Mensaje enviado correctamente');
            this.mensaje = '';
            this.selectedUsuario = null;
            this.selectedPerfil = null;
            this.todosPerfiles = false;
            this.cargarMensajes();
          } else {
            this.swalService.error('Error al enviar mensaje');
            console.error('Error al enviar mensaje:', response);
          }
        },
        error: (error) => {
          this.swalService.error('Error al enviar mensaje');
          console.error('Error al enviar mensaje:', error);
        }
      });
  }

  onPerfilChange(event: any): void {
    if (event.value) {
      this.selectedUsuario = null;
    }
  }

  onUsuarioChange(event: any): void {
    if (event.value) {
      this.selectedPerfil = null;
    }
  }

  get isPerfilDisabled(): boolean {
    return !!this.selectedUsuario;
  }

  get isUsuarioDisabled(): boolean {
    return !!this.selectedPerfil;
  }

  buscarMensajes(): void {
    // Si no hay fecha seleccionada, usar la fecha actual
    if (!this.fechaDesde) {
      this.fechaDesde = new Date();
    }

    this.loading = true;
    const fechaFormateada = this.formatearFecha(this.fechaDesde);
    const request = {
      sUsuario: this.sessionService.getUserSession().replace(/['"]+/g, ''),
      sFecha: fechaFormateada
    };

    if (this.tipoSeleccionado === 'recibidos') {
      this.centroMensajesService.getMensajesNotificados(request)
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.mensajes = response.data;
              this.dataSource.data = response.data;
            }
          },
          error: (error) => {
            console.error('Error al buscar mensajes recibidos:', error);
          }
        });
    } else {
      this.centroMensajesService.getMensajesEnviados(request)
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.mensajes = response.data;
              this.dataSource.data = response.data;
            }
          },
          error: (error) => {
            console.error('Error al buscar mensajes enviados:', error);
          }
        });
    }
  }

  private formatearFecha(fecha: Date | string): string {
    if (!fecha) return '';

    // Si es string (viene del input date), convertir el formato YYYY-MM-DD a DD/MM/YYYY
    if (typeof fecha === 'string') {
      const [año, mes, dia] = fecha.split('-');
      return `${dia}/${mes}/${año}`;
    }

    // Si es un objeto Date
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    return `${dia}/${mes}/${año}`;
  }

  private cargarMensajes(): void {
    this.buscarMensajes();
  }

  close(): void {
    if (this.data?.onClose) {
      this.data.onClose();
    }
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
} 